import type { ApiClient } from '@carbonwebmail/api-client'
import type { DashboardDto } from '@carbonwebmail/contracts'
import { BOUNCE_RATE_LIMIT, COMPLAINT_RATE_LIMIT } from '@carbonwebmail/contracts'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Async } from '../../ui/Async.js'
import { count, percent } from '../../ui/format.js'
import type { PillTone } from '../../ui/StatusPill.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { useDashboard } from './useDashboard.js'

/**
 * Sending health.
 *
 * Four numbers and two rates, and the choice of which four is the product.
 * Every other tool leads with "delivered" — a number that is always large and
 * always reassuring. This leads with what needs a person: what is waiting, what
 * came back **unknown**, and what has stopped being retried.
 *
 * The view holds no state and calls no client. Everything comes from
 * `useDashboard`.
 */
export function DashboardPage({ client }: { readonly client: ApiClient }) {
  const { state, retry } = useDashboard({ client })

  return (
    <>
      <header className="mb-6">
        <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Sending health</h1>
        <p className="mt-1 text-[13px] text-t2">The last seven days.</p>
      </header>

      <Async state={state} skeleton={<Skeleton />} onRetry={() => void retry()}>
        {(summary) => <Summary summary={summary} />}
      </Async>
    </>
  )
}

function Summary({ summary }: { readonly summary: DashboardDto }) {
  const nothingSent = summary.sentThisWeek === 0 && summary.queued === 0 && summary.claimed === 0

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          label="Sent this week"
          value={summary.sentThisWeek}
          sub={
            summary.campaignsThisWeek === 0
              ? 'no campaigns yet'
              : `across ${count(summary.campaignsThisWeek)} ${
                  summary.campaignsThisWeek === 1 ? 'campaign' : 'campaigns'
                }`
          }
          chip="accepted"
          tone="ok"
        />
        <Tile
          label="Queue depth"
          value={summary.queued + summary.claimed}
          sub={`claimed ${count(summary.claimed)} · queued ${count(summary.queued)}`}
          chip="live"
          tone="neutral"
        />
        {/*
          The tile the product is for. `unknown` is never retried, because the
          provider may already have delivered it — so it sits here until someone
          reconciles it, and it is never painted as an error.
        */}
        <Tile
          label="Unknown"
          value={summary.unknown}
          sub="never retried — needs checking"
          chip={summary.unknown === 0 ? 'clear' : 'action'}
          tone={summary.unknown === 0 ? 'neutral' : 'unknown'}
        />
        <Tile
          label="Dead-lettered"
          value={summary.deadLettered}
          sub="retries exhausted"
          chip={summary.deadLettered === 0 ? 'clear' : 'blocked'}
          tone={summary.deadLettered === 0 ? 'neutral' : 'danger'}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Rate
          label="Bounce rate (7d)"
          rate={summary.bounceRate}
          limit={BOUNCE_RATE_LIMIT}
          measured={summary.sentThisWeek}
        />
        <Rate
          label="Complaint rate (7d)"
          rate={summary.complaintRate}
          limit={COMPLAINT_RATE_LIMIT}
          measured={summary.sentThisWeek}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Audience">
          <Figure label="Subscribed" value={summary.subscribedContacts} />
          <Figure label="All contacts" value={summary.totalContacts} />
          <Figure label="Suppressed" value={summary.suppressed} />
          <Link className="mt-4 inline-block font-semibold text-[13px] text-brand" to="/contacts">
            Open contacts
          </Link>
        </Panel>

        <Panel title={nothingSent ? 'Nothing has been sent yet' : 'Recent sending'}>
          {nothingSent ? (
            <p className="text-[13px] text-t2 leading-relaxed">
              Import contacts, verify a sending domain, then write your first campaign. Verification
              takes a few minutes; DNS can take an hour.
            </p>
          ) : (
            <p className="text-[13px] text-t2 leading-relaxed">
              <span className="font-num font-semibold text-t1 tabular-nums">
                {count(summary.sentThisWeek)}
              </span>{' '}
              accepted in the last seven days. Per-campaign reporting is not built yet, so this is
              the whole picture for now.
            </p>
          )}
        </Panel>
      </div>
    </>
  )
}

function Tile({
  label,
  value,
  sub,
  chip,
  tone,
}: {
  readonly label: string
  readonly value: number
  readonly sub: string
  readonly chip: string
  readonly tone: PillTone
}) {
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-semibold text-[12px] text-t2">{label}</span>
        <StatusPill tone={tone}>{chip}</StatusPill>
      </div>
      <div className="font-bold font-num text-[28px] text-t1 tabular-nums">{count(value)}</div>
      <div className="mt-1 text-[12px] text-t3">{sub}</div>
    </div>
  )
}

/**
 * A rate against the level at which mailbox providers start suspending
 * accounts.
 *
 * It says who acts, because nothing pauses sending automatically yet. A bar
 * that implied an automatic protection would be the most expensive kind of
 * untrue statement in the product.
 */
/**
 * The bar's width, in tenths, as literal class names.
 *
 * Not `style={{ width }}`: inline styles are a build failure here, because a
 * style attribute is a place a rule can hide from the design tokens. And not a
 * template like `w-[${n}%]` either — Tailwind generates classes by scanning
 * source text, so a class assembled at runtime is a class that does not exist
 * in the stylesheet and silently renders at zero width.
 *
 * Tenths are enough: this is a proportion bar, not a readout. The exact figure
 * is printed beside it.
 */
const WIDTHS = [
  'w-0',
  'w-[10%]',
  'w-[20%]',
  'w-[30%]',
  'w-[40%]',
  'w-[50%]',
  'w-[60%]',
  'w-[70%]',
  'w-[80%]',
  'w-[90%]',
  'w-full',
] as const

function Rate({
  label,
  rate,
  limit,
  measured,
}: {
  readonly label: string
  readonly rate: number
  readonly limit: number
  readonly measured: number
}) {
  const share = Math.min(1, rate / limit)
  const tone: PillTone = rate >= limit ? 'danger' : share >= 0.8 ? 'warn' : 'ok'
  const bar = { danger: 'bg-danger', warn: 'bg-warn', ok: 'bg-ok', unknown: '', neutral: '' }[tone]
  const width = WIDTHS[Math.round(share * 10)] ?? WIDTHS[0]

  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-semibold text-[12px] text-t2">{label}</span>
        <span className="font-bold font-num text-[15px] text-t1 tabular-nums">
          {measured === 0 ? '—' : percent(rate, 2)}
        </span>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-pill bg-surface-2">
        <div className={`h-full rounded-pill ${bar} ${width}`} />
      </div>

      <p className="mt-2 text-[12px] text-t3">
        {measured === 0
          ? 'Nothing sent this week, so there is no rate to report.'
          : `Providers suspend accounts above ${percent(limit, 2)}. Sending is not paused automatically — that is still a person's call.`}
      </p>
    </div>
  )
}

function Panel({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <div className="rounded-card border border-line bg-surface p-5">
      <h2 className="mb-3 font-bold text-[14px] text-t1">{title}</h2>
      {children}
    </div>
  )
}

function Figure({ label, value }: { readonly label: string; readonly value: number }) {
  return (
    <div className="flex items-baseline justify-between border-line border-b py-2 last:border-b-0">
      <span className="text-[13px] text-t2">{label}</span>
      <span className="font-num font-semibold text-[14px] text-t1 tabular-nums">
        {count(value)}
      </span>
    </div>
  )
}

function Skeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {['a', 'b', 'c', 'd'].map((key) => (
        <div
          key={key}
          className="h-[104px] animate-pulse rounded-card border border-line bg-surface-2"
        />
      ))}
    </div>
  )
}
